import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { resolveEditTemplatePreviewColumnIds } from '@/tableDefinitionEditor/editTemplatePreviewColumn.utilities';
import type {
  ColumnDefinition,
  ItemTableCell,
  MaterializedTableHeader,
  MaterializedTableOutput,
  ProcessedMaterializedTable,
  ProcessedTableCell,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColumnDefinitionBulkEditInputV1, ColumnDefinitionInputV1 } from '@seeqdev/api/model/models';

const getHeaderName = (header: MaterializedTableHeader) => header.name ?? '';

const buildHeaderIndexByName = (headers: MaterializedTableHeader[]) => {
  const indexByName = new Map<string, number>();
  headers.forEach((header, index) => {
    const name = getHeaderName(header);
    if (name) {
      indexByName.set(name, index);
    }
  });
  return indexByName;
};

export const getPreviewColumnNamesForMaterializedMerge = (
  previewColumnIds: string[],
  previewTableColumns: ColumnDefinition[],
): Set<string> => {
  const previewColumnIdSet = new Set(previewColumnIds.map((columnId) => columnId.toLowerCase()));
  return new Set(
    previewTableColumns
      .filter((column) => previewColumnIdSet.has(column.id.toLowerCase()))
      .map((column) => column.columnName),
  );
};

const getItemIdFromProcessedRow = (row: ProcessedTableCell[], itemIdIndex: number): string | undefined => {
  if (itemIdIndex < 0) {
    return undefined;
  }
  const cell = row[itemIdIndex] as ItemTableCell | undefined;
  return cell?.uuid?.toLowerCase();
};

const findIncomingRowIndex = (
  incomingRows: ProcessedTableCell[][],
  itemId: string | undefined,
  incomingItemIdIndex: number,
  fallbackRowIndex: number,
): number => {
  if (itemId && incomingItemIdIndex >= 0) {
    const matchedIndex = incomingRows.findIndex(
      (row) => getItemIdFromProcessedRow(row, incomingItemIdIndex) === itemId,
    );
    if (matchedIndex >= 0) {
      return matchedIndex;
    }
  }
  return fallbackRowIndex < incomingRows.length ? fallbackRowIndex : -1;
};

const shouldPreserveColumnOnMaterializedMerge = (
  headerName: string,
  previewColumnNames: Set<string>,
  incomingHeaderIndexByName: Map<string, number>,
): boolean => previewColumnNames.has(headerName) || !incomingHeaderIndexByName.has(headerName);

/** Applies a materialized-table fetch during edit mode without overwriting preview column cell data. */
export const mergeProcessedMaterializedTablePreservingPreviewColumns = (
  existingTable: ProcessedMaterializedTable,
  incomingTable: ProcessedMaterializedTable,
  previewColumnNames: Set<string>,
): ProcessedMaterializedTable => {
  const incomingHeaderIndexByName = buildHeaderIndexByName(incomingTable.headers);
  const existingItemIdIndex = existingTable.headers.findIndex(
    (header) => getHeaderName(header) === SeeqNames.MaterializedTables.ItemIdColumn,
  );
  const incomingItemIdIndex = incomingTable.headers.findIndex(
    (header) => getHeaderName(header) === SeeqNames.MaterializedTables.ItemIdColumn,
  );

  const mergedRows = existingTable.rows.map((existingRow, rowIndex) => {
    const itemId = getItemIdFromProcessedRow(existingRow, existingItemIdIndex);
    const incomingRowIndex = findIncomingRowIndex(incomingTable.rows, itemId, incomingItemIdIndex, rowIndex);
    const incomingRow = incomingRowIndex >= 0 ? incomingTable.rows[incomingRowIndex] : undefined;

    return existingTable.headers.map((header, columnIndex) => {
      const headerName = getHeaderName(header);
      if (shouldPreserveColumnOnMaterializedMerge(headerName, previewColumnNames, incomingHeaderIndexByName)) {
        return existingRow[columnIndex];
      }

      const incomingColumnIndex = incomingHeaderIndexByName.get(headerName);
      if (incomingRow && incomingColumnIndex !== undefined) {
        return incomingRow[incomingColumnIndex];
      }

      return existingRow[columnIndex];
    });
  });

  return {
    headers: existingTable.headers,
    rows: mergedRows,
    hasMore: incomingTable.hasMore || existingTable.hasMore,
  };
};

export const mergeMaterializedTableDuringTemplateEdit = ({
  existingTable,
  incomingRawTable,
  persistedColumns,
  previewTableColumns,
  editTemplatePreviewColumns,
  processMaterializedTable,
}: {
  existingTable: ProcessedMaterializedTable;
  incomingRawTable: MaterializedTableOutput;
  persistedColumns: ColumnDefinition[];
  previewTableColumns: ColumnDefinition[];
  editTemplatePreviewColumns: (ColumnDefinitionInputV1 | ColumnDefinitionBulkEditInputV1)[] | undefined;
  processMaterializedTable: (
    raw: MaterializedTableOutput,
    columns: ColumnDefinition[],
  ) => ProcessedMaterializedTable | undefined;
}): ProcessedMaterializedTable | undefined => {
  const incomingProcessed = processMaterializedTable(incomingRawTable, persistedColumns);
  if (!incomingProcessed) {
    return existingTable;
  }

  const previewColumnIds = resolveEditTemplatePreviewColumnIds(editTemplatePreviewColumns, previewTableColumns);
  const previewColumnNames = getPreviewColumnNamesForMaterializedMerge(previewColumnIds, previewTableColumns);

  return mergeProcessedMaterializedTablePreservingPreviewColumns(existingTable, incomingProcessed, previewColumnNames);
};
